/** 场景与客流配置页（F-001/004/005/006 + F-003）。参考 v0 设计布局。 */
import { useMemo, useRef, useState } from 'react';
import { Building2, Plus, Trash2, Download, Upload, Save, Folder, AlertCircle, CheckCircle2, ChevronRight, Home, Briefcase, Hotel, Plane } from 'lucide-react';
import { ExperimentConfigSchema } from '../../core/config-schema';
import { buildExperimentConfig, pickSetup, useSetup, type SegmentDraft } from '../store';
import { TEMPLATES } from '../../data/templates';
import { useLibrary } from '../library-store';
import { serializeScenario, parseScenarioFile } from '../../data/scenario-file';
import { downloadText } from '../../data/download';
import { Section, NumInput, FormRow } from '../primitives';
import { cn } from '../cn';

const PRESET_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  'residential-morning': Home,
  'office-all-day': Briefcase,
  hotel: Hotel,
  'airport-hub': Plane,
};

export function ScenarioConfigPage() {
  const s = useSetup();
  const { scenarios, addScenario, removeScenario } = useLibrary();
  const [scnName, setScnName] = useState('');
  const [scnErr, setScnErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const modeSum = s.upPeak + s.downPeak + s.interfloor;
  const error = useMemo(() => {
    if (Math.abs(modeSum - 1) > 1e-9) return `交通模式比例之和须为 100%（当前 ${(modeSum * 100).toFixed(0)}%）`;
    const r = ExperimentConfigSchema.safeParse(buildExperimentConfig(s));
    if (!r.success) return r.error.issues[0]?.message ?? '配置非法';
    return null;
  }, [s, modeSum]);

  const setElev = (p: Partial<typeof s.elevator>) => s.patch({ elevator: { ...s.elevator, ...p } });
  const setSeg = (i: number, p: Partial<SegmentDraft>) => s.patch({ segments: s.segments.map((seg, idx) => (idx === i ? { ...seg, ...p } : seg)) });
  const addSeg = () => s.patch({ segments: [...s.segments, { startSec: 0, endSec: 3600, arrivalRatePer5min: 20 }] });
  const removeSeg = (i: number) => s.patch({ segments: s.segments.filter((_, idx) => idx !== i) });

  const saveCurrent = () => {
    const name = scnName.trim();
    if (!name) { setScnErr('请输入场景名'); return; }
    addScenario(name, pickSetup(s));
    setScnErr(null);
  };
  const onImport = async (file: File) => {
    try {
      const { name, setup } = parseScenarioFile(await file.text());
      useSetup.setState({ ...setup });
      setScnName(name);
      setScnErr(null);
    } catch (e) { setScnErr(e instanceof Error ? e.message : '导入失败'); }
  };

  return (
    <div className="flex flex-col gap-5 max-w-5xl mx-auto">
      {/* 预设模板 */}
      <Section title="预设场景模板" icon={Building2}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TEMPLATES.map((t) => {
            const Icon = PRESET_ICON[t.id] ?? Building2;
            return (
              <button
                key={t.id}
                onClick={() => useSetup.setState({ ...t.setup })}
                className="group flex flex-col items-start gap-2 p-3.5 rounded-lg border border-border bg-secondary/40 hover:border-primary/40 hover:bg-primary/5 transition-all"
              >
                <Icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-sm font-medium text-foreground">{t.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{t.setup.floors}F · {t.setup.elevatorCount}台</span>
              </button>
            );
          })}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* 我的场景 */}
        <Section title="我的场景" icon={Folder}>
          <div className="flex gap-2">
            <input
              value={scnName}
              onChange={(e) => setScnName(e.target.value)}
              placeholder="场景名称"
              className="flex-1 h-8 bg-input/40 border border-border rounded px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60"
            />
            <button onClick={saveCurrent} className="h-8 px-3 flex items-center gap-1 text-xs font-medium rounded bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25 transition-colors">
              <Save className="w-3.5 h-3.5" />保存
            </button>
          </div>
          <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
            {scenarios.length === 0 && <p className="text-[12px] text-muted-foreground text-center py-4">暂无保存场景</p>}
            {scenarios.map((sc) => (
              <div key={sc.name} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-secondary/50 border border-border/60 hover:border-border transition-colors">
                <button onClick={() => { useSetup.setState({ ...sc.setup }); setScnName(sc.name); }} className="text-sm text-foreground hover:text-primary transition-colors text-left truncate flex-1">{sc.name}</button>
                <button onClick={() => downloadText(`scenario-${sc.name}.json`, serializeScenario(sc.name, sc.setup), 'application/json')} className="text-muted-foreground hover:text-foreground transition-colors" title="导出"><Download className="w-3 h-3" /></button>
                <button onClick={() => removeScenario(sc.name)} className="text-muted-foreground hover:text-destructive transition-colors" title="删除"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t border-border">
            <button onClick={() => downloadText(`scenario-${scnName.trim() || 'untitled'}.json`, serializeScenario(scnName.trim() || '场景', pickSetup(s)), 'application/json')} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Download className="w-3.5 h-3.5" />导出 JSON
            </button>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Upload className="w-3.5 h-3.5" />导入 JSON
            </button>
            <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = ''; }} />
            {scnErr && <p className="text-[11px] text-destructive">{scnErr}</p>}
          </div>
        </Section>

        {/* 参数 */}
        <div className="lg:col-span-2 flex flex-col gap-5">
          <Section title="建筑与电梯参数" icon={Building2}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FormRow label="楼层数" id="input-floors"><NumInput id="input-floors" value={s.floors} min={2} max={120} unit="F" onChange={(v) => s.patch({ floors: v })} /></FormRow>
              <FormRow label="层高"><NumInput value={s.floorHeight} min={1} step={0.1} unit="m" onChange={(v) => s.patch({ floorHeight: v })} /></FormRow>
              <FormRow label="电梯台数"><NumInput value={s.elevatorCount} min={1} max={48} onChange={(v) => s.patch({ elevatorCount: v })} /></FormRow>
              <FormRow label="轿厢容量"><NumInput value={s.elevator.capacity} min={1} unit="人" onChange={(v) => setElev({ capacity: v })} /></FormRow>
              <FormRow label="额定速度"><NumInput value={s.elevator.ratedSpeed} min={0.1} step={0.1} unit="m/s" onChange={(v) => setElev({ ratedSpeed: v })} /></FormRow>
              <FormRow label="加速度"><NumInput value={s.elevator.acceleration} min={0.1} step={0.1} unit="m/s²" onChange={(v) => setElev({ acceleration: v })} /></FormRow>
              <FormRow label="开门时间"><NumInput value={s.elevator.doorOpenTime} min={0} step={100} unit="ms" onChange={(v) => setElev({ doorOpenTime: v })} /></FormRow>
              <FormRow label="关门时间"><NumInput value={s.elevator.doorCloseTime} min={0} step={100} unit="ms" onChange={(v) => setElev({ doorCloseTime: v })} /></FormRow>
              <FormRow label="每客上梯"><NumInput value={s.elevator.boardTimePerPax} min={0} step={50} unit="ms" onChange={(v) => setElev({ boardTimePerPax: v })} /></FormRow>
              <FormRow label="随机种子"><NumInput value={s.seed} min={0} onChange={(v) => s.patch({ seed: v })} /></FormRow>
            </div>
          </Section>

          <Section title="交通模式（合计须 100%）">
            <div className="grid grid-cols-3 gap-3">
              <FormRow label="上行高峰"><NumInput value={s.upPeak} min={0} max={1} step={0.05} onChange={(v) => s.patch({ upPeak: v })} /></FormRow>
              <FormRow label="下行高峰"><NumInput value={s.downPeak} min={0} max={1} step={0.05} onChange={(v) => s.patch({ downPeak: v })} /></FormRow>
              <FormRow label="层间交通"><NumInput value={s.interfloor} min={0} max={1} step={0.05} onChange={(v) => s.patch({ interfloor: v })} /></FormRow>
            </div>
            <div className={cn('flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded', Math.abs(modeSum - 1) < 1e-9 ? 'text-chart-3 bg-chart-3/10' : 'text-destructive bg-destructive/10')}>
              {Math.abs(modeSum - 1) < 1e-9 ? <><CheckCircle2 className="w-3 h-3" />合计 100% — 校验通过</> : <><AlertCircle className="w-3 h-3" />合计 {(modeSum * 100).toFixed(0)}%，需等于 100%</>}
            </div>
          </Section>

          <Section title="客流时段配置">
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-x-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1">
                <span>起始(s)</span><span>结束(s)</span><span>到达人数/5min</span><span />
              </div>
              {s.segments.map((seg, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                  <input type="number" value={seg.startSec} min={0} step={60} onChange={(e) => setSeg(i, { startSec: Number(e.target.value) })} className="h-8 bg-input/40 border border-border rounded px-2 text-sm num text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60" />
                  <input type="number" value={seg.endSec} min={1} step={60} onChange={(e) => setSeg(i, { endSec: Number(e.target.value) })} className="h-8 bg-input/40 border border-border rounded px-2 text-sm num text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60" />
                  <input type="number" value={seg.arrivalRatePer5min} min={0} onChange={(e) => setSeg(i, { arrivalRatePer5min: Number(e.target.value) })} className="h-8 bg-input/40 border border-border rounded px-2 text-sm num text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60" />
                  <button onClick={() => removeSeg(i)} className="w-8 h-8 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={addSeg} className="flex items-center gap-1.5 mt-1 text-[12px] text-primary hover:text-primary/80 transition-colors"><Plus className="w-3.5 h-3.5" />添加时段</button>
            </div>
          </Section>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/30 bg-destructive/5 text-sm text-destructive">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => { window.location.hash = '#/strategy'; }}
          disabled={!!error}
          className={cn(
            'flex items-center gap-2 h-9 px-5 rounded text-sm font-semibold transition-all',
            error ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-primary text-primary-foreground hover:bg-primary/85 shadow-[0_0_16px_oklch(0.72_0.165_196/20%)]',
          )}
        >
          下一步：选择策略<ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
